import { useEffect, useState } from "react";

import { Web3Provider } from "@ethersproject/providers";
import { useWeb3React } from "@web3-react/core";

import { injected } from "../connectors";
import { NetworkContextName } from "../constants/misc";
import { EthereumProvider } from "../lib/ethereum";

export function useActiveWeb3React() {
	const context = useWeb3React<Web3Provider>()
	const contextNetwork = useWeb3React<Web3Provider>(NetworkContextName)
	return context.active ? context : contextNetwork
}

export function useInactiveListener(suppress: boolean = false) {
	const { active, error, activate } = useWeb3React()

	useEffect((): any => {
		const ethereum = window as EthereumProvider | undefined

		if (ethereum && ethereum.on && !active && !error && !suppress) {
			const handleChainChanged = () => {
				// eat errors
				activate(injected, undefined, true).catch((error) => {
					console.error('Failed to activate after chain changed', error)
				})
			}

			const handleAccountsChanged = (accounts: string[]) => {
				if (accounts.length > 0) {
					// eat errors
					activate(injected, undefined, true).catch((error) => {
						console.error('Failed to activate after accounts changed', error)
					})
				}
			}

			ethereum.on('chainChanged', handleChainChanged)
			ethereum.on('accountsChanged', handleAccountsChanged)

			return () => {
				if (ethereum.removeListener) {
					ethereum.removeListener('chainChanged', handleChainChanged)
					ethereum.removeListener('accountsChanged', handleAccountsChanged)
				}
			}
		}

		return undefined
	}, [active, error, suppress, activate])
}

export function useEagerConnect() {
	const { activate, active } = useWeb3React()
	const [tried, setTried] = useState(false)

	useEffect(() => {
		if (!active) {
			injected.isAuthorized().then((isAuthorized) => {
				if (isAuthorized) {
					activate(injected, undefined, true).catch(() => {
						setTried(true)
					})
				} else {
					if (window.ethereum) {
						activate(injected, undefined, true).catch(() => {
							setTried(true)
						})
					} else {
						setTried(true)
					}
				}
			})
		}
	}, [activate, active])

	// if the connection worked, wait until we get confirmation of that to flip the flag
	useEffect(() => {
		if (!tried && active) {
			setTried(true)
		}
	}, [tried, active])

	return tried
}
